import type { CommentableContentObject, ContentObjectList, NonEventContentObjectDetail } from 'models';
import { isGroupContentObject } from 'models';
import { type RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { usePermissions } from 'permissions';
import type { InputRef, RichTextEditorBase } from 'ui';
import { createCommentContentObject } from '../utils';

type P = {
  forceCommentsOpen?: boolean;
  item: ContentObjectList | NonEventContentObjectDetail;
};

type UseContentObjectCommentsReturn = {
  commentContentObject?: CommentableContentObject;
  commentFormContainerRef: RefObject<HTMLDivElement | null>;
  editorRef: RefObject<null | RichTextEditorBase>;
  inputRef: RefObject<InputRef | null>;
  isCommentable: boolean;
  isModalOpened: boolean;
  onCloseModal: () => void;
  onShowComments?: () => void;
};

export const useContentObjectComments = ({ forceCommentsOpen, item }: P): UseContentObjectCommentsReturn => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const editorRef = useRef<RichTextEditorBase>(null);
  const inputRef = useRef<InputRef>(null);
  const commentFormContainerRef = useRef<HTMLDivElement>(null);

  const { canCommentContentObjectList } = usePermissions();

  const commentContentObject = useMemo(() => createCommentContentObject(item), [item]);
  const canComment = commentContentObject ? canCommentContentObjectList({ contentObject: item }) : false;
  const hasVisibleComments = item.commentsCount > 0;
  const isCommentable =
    Boolean(commentContentObject) && !isGroupContentObject(item) && (canComment || hasVisibleComments);

  const onShowComments = useCallback(() => {
    if (isModalOpened || forceCommentsOpen) {
      editorRef.current?.focus() ?? inputRef.current?.focus();
      commentFormContainerRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setIsModalOpened(true);
  }, [forceCommentsOpen, isModalOpened]);

  const onCloseModal = useCallback(() => {
    setIsModalOpened(false);
  }, []);

  return useMemo(
    () => ({
      commentContentObject,
      commentFormContainerRef,
      editorRef,
      inputRef,
      isCommentable,
      isModalOpened,
      onCloseModal,
      onShowComments: isCommentable ? onShowComments : undefined,
    }),
    [commentContentObject, isCommentable, isModalOpened, onCloseModal, onShowComments],
  );
};
